<script setup lang="ts">
import LZS from 'lz-string'
import CustomChartCreator from '@/components/charts/chart-creator/CustomChartCreator.vue'
import ChartSuggestionPicker from '@/components/charts/chart-creator/ChartSuggestionPicker.vue'
import { computed, ComputedRef, Ref, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useI18n } from 'vue-i18n'
import { SerializedChartType } from '@/composables/charts/influx-charts/chart-builders/ChartInterfaces'
import { useLogsStore } from '@/store/logs'
import _ from 'lodash'

const props = withDefaults(
  defineProps<{
    hideActions?: boolean
    showSuggestion?: boolean
  }>(),
  {
    hideActions: false,
    showSuggestion: true,
  }
)
const serializedChart = defineModel<SerializedChartType | null>('chart', { default: null })
const compressedChart = defineModel<string | null>('compressed', { default: null })
const stepper = defineModel<number>('stepper', { default: 1 })
defineEmits(['cancel', 'save'] as const)

const { mobile } = useDisplay()
const { t } = useI18n()

const items = [
  {
    index: 1,
    title: t('chart_sharing.creator.stepper.suggestion'),
    icon: 'mdi-lightbulb-on-outline',
    contentTitle: t('chart_sharing.creator.stepper.heading.suggestion'),
    subtitle: t('chart_sharing.creator.stepper.subtitle.suggestion'),
  },
  {
    index: 2,
    title: t('chart_sharing.creator.stepper.customize'),
    icon: 'mdi-palette-outline',
    contentTitle: t('chart_sharing.creator.stepper.heading.customize'),
    subtitle: t('chart_sharing.creator.stepper.subtitle.customize'),
  },
]

const internalModel: Ref<SerializedChartType | null> = ref(null)

const compiledCompressChart: ComputedRef<string | null> = computed(() => {
  if (!internalModel.value) return null
  try {
    return LZS.compressToEncodedURIComponent(JSON.stringify(internalModel.value))
  } catch {
    return null
  }
})

const isFirstStep = computed(() => stepper.value === 1)
const isFinalStep = computed(() => stepper.value === items.length)

async function jumpToStep(step: number) {
  stepper.value = step
}

watch(
  () => [serializedChart.value, compressedChart.value],
  () => {
    if (serializedChart.value && compressedChart.value) {
      useLogsStore().addLogEntry({
        tag: 'chartCreator',
        message: 'Both serialized and compressed chart data are present. This should not happen.',
        level: 'ERROR',
      })
    }
    if (props.showSuggestion && !serializedChart.value && !compressedChart.value) {
      stepper.value = 1
      return
    }
    stepper.value = 2
  },
  { immediate: true, once: true }
)

watch(
  internalModel,
  () => {
    if (!_.isEqual(internalModel.value, serializedChart.value)) {
      serializedChart.value = internalModel.value
    }

    if (compiledCompressChart.value !== compressedChart.value) {
      compressedChart.value = compiledCompressChart.value
    }
  },
  { deep: true, immediate: true }
)

watch(
  compressedChart,
  () => {
    if (compressedChart.value === null) return
    if (compressedChart.value === compiledCompressChart.value) return
    try {
      const decompressed: string = LZS.decompressFromEncodedURIComponent(compressedChart.value)
      internalModel.value = decompressed ? (JSON.parse(decompressed) as SerializedChartType) : null
    } catch {
      internalModel.value = null
    }
  },
  { deep: true, immediate: true }
)

watch(
  serializedChart,
  () => {
    if (serializedChart.value === null) return
    if (_.isEqual(internalModel.value, serializedChart.value)) return
    internalModel.value = serializedChart.value
  },
  { deep: true, immediate: true }
)
</script>

<template>
  <v-stepper
    id="chart-creator-stepper"
    v-model="stepper"
    :mobile="mobile"
    :items="items"
    non-linear
    elevation="0"
    hide-actions
    editable
    :item-props="() => ({ class: 'rounded-lg', ripple: false })"
    selected-class="font-weight-bold text-primary"
    :class="{ 'is-mobile': mobile }"
  >
    <template #icon="{ step }">
      <v-avatar :color="stepper === step ? 'primary' : 'surface-variant'">
        <v-icon :icon="items[step - 1].icon" />
      </v-avatar>
    </template>
    <template #[`item.1`]>
      <v-card-title class="font-weight-bold text-center">
        {{ items[0].contentTitle }}
      </v-card-title>
      <p class="mb-4 text-body-1 text-center font-italic opacity-75 d-flex align-center justify-center ga-2">
        <v-avatar
          size="x-small"
          variant="tonal"
          color="primary"
        >
          <v-icon
            :icon="items[0].icon"
            size="x-small"
          />
        </v-avatar>
        {{ items[0].subtitle }}
      </p>

      <ChartSuggestionPicker @selected="internalModel = $event" />
    </template>
    <template #[`item.2`]>
      <v-card-title class="font-weight-bold text-center">
        {{ items[1].contentTitle }}
      </v-card-title>
      <p class="mb-4 text-body-1 text-center font-italic opacity-75 d-flex align-center justify-center ga-2">
        <v-avatar
          size="x-small"
          variant="tonal"
          color="primary"
        >
          <v-icon
            :icon="items[1].icon"
            size="x-small"
          />
        </v-avatar>
        {{ items[1].subtitle }}
      </p>

      <CustomChartCreator
        v-model="internalModel"
        :show-preview="false"
      />
    </template>
  </v-stepper>

  <v-card-actions
    v-if="!props.hideActions"
    :class="isFirstStep ? 'justify-end' : 'justify-space-between'"
  >
    <v-btn
      v-if="!isFirstStep"
      prepend-icon="mdi-chevron-left"
      variant="tonal"
      @click="jumpToStep(1)"
    >
      {{ t('general_interface.buttons.back') }}
    </v-btn>
    <v-btn
      :append-icon="isFinalStep ? 'mdi-check' : 'mdi-chevron-right'"
      variant="tonal"
      color="primary"
      @click="isFinalStep ? $emit('save') : jumpToStep(2)"
    >
      {{ isFirstStep ? t('general_interface.buttons.next') : t('general_interface.buttons.save') }}
    </v-btn>
  </v-card-actions>
</template>

<style>
#chart-creator-stepper .v-stepper-header {
  box-shadow: none;
}
#chart-creator-stepper .v-stepper-window {
  margin: 0rem 1rem;
}
#chart-creator-stepper.is-mobile .v-stepper-window {
  margin: 0rem;
}

#chart-creator-stepper .v-stepper-item {
  padding: 1rem;
}
</style>
